import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';

import { FormControl, FormGroup, ValidatorFn } from '@angular/forms';

import { Subject, Subscription } from 'rxjs';

import * as moment from 'moment';

import { FormComponent } from '../form';
import { ComponentTypesEnum } from '../../enums';
import {FilterControl, BaseControl, SelectControl, DatepickerControl} from '../../classes';

import { Pagination } from 'appShared/components';
import { ChangeFilterControlValuePairInterface } from 'appShared/interfaces';

export const ItemsPerPageSelector = 'itemsPerPage';
export const DefaultItemsPerPage = Pagination.defaultItemsPerPage;

@Component({
    selector: 'app-filter',
    templateUrl: './filter.component.html',
    styleUrls: ['./filter.component.scss']
})
export class FilterComponent implements OnInit, OnDestroy {
    private readonly subscriptionsArray: Subscription[];

    private itemsPerPageControl: FilterControl;
    private initializedControls: number;

    public isSettingsShown: boolean;
    @Input() public form: FormGroup;
    public componentTypes: typeof ComponentTypesEnum;

    @Input() public controls: FilterControl[];
    @Input() public excludePaginationControls: boolean;
    @Input() public excludeFilterChangeOnClear: boolean;
    @Input() public setDatePickersToNowOnClear: boolean;
    @Input() public paginationOptions: number[];
    @Input() public defaultPaginationOption: number;
    @Input() public cachedPaginationOption: number;
    @Input() public isStatic: boolean;
    @Input() public wantsValidation: boolean;
    @Input() public controlsValidationChanged: Subject<{ name: string, validators: ValidatorFn[] }>;
    @Input() public updateValueAndValidity: Subject<{name: string, validators: ValidatorFn[], value:any, opts?:{onlySelf?: boolean; emitEvent?: boolean}}>;
    @Input() public formControlSmallerWidth: boolean;
    @Input() public controlsValueChangePair: ChangeFilterControlValuePairInterface;

    @Output() public filterChanged: EventEmitter<{ [key: string]: string | number | boolean }>;
    @Output() public clearTriggered: EventEmitter<{ [key: string]: string | number | boolean }>;
    @Output() public valueChanged: EventEmitter<{ [key: string]: string | number | boolean }>;
    @Output() public afterInit: EventEmitter<boolean>;

    @ViewChild('filterControlsBox', { static: true }) public filterControlsBoxRef: ElementRef;

    constructor() {
        this.subscriptionsArray = [];
        this.componentTypes = ComponentTypesEnum;
        this.filterChanged = new EventEmitter();
        this.clearTriggered = new EventEmitter();
        this.valueChanged = new EventEmitter();
        this.afterInit = new EventEmitter();
        this.isSettingsShown = false;
        this.initializedControls = 0;
        this.paginationOptions = [10, 50, 100, 200];
        this.setDatePickersToNowOnClear = true;
        this.excludePaginationControls = false;
        if (!this.controlsValidationChanged) {
            this.controlsValidationChanged = new Subject();
        }
        if (!this.updateValueAndValidity) {
            this.updateValueAndValidity = new Subject();
        }
    }

    private initFormAndControls(): void {
        if (!this.controls) {
            this.controls = [];
        }

        if (!this.excludePaginationControls) {
            this.itemsPerPageControl = this.initItemsPerPageFormControl();

            this.itemsPerPageControl.isRemovable = false;
            this.itemsPerPageControl.sortIndex = this.controls.length + 1;

            this.controls.push(this.itemsPerPageControl);
        }

        this.controls.sort((previous: FilterControl, next: FilterControl) => previous.sortIndex - next.sortIndex)
            .map((control: FilterControl) => {
                control.controls.sort((previous: BaseControl, next: BaseControl) => {
                    return previous.sortIndex - next.sortIndex;
                }).forEach((formsControl: BaseControl) => {
                    this.form.addControl(formsControl.name, new FormControl(formsControl.value, formsControl.validators));
                });
            });
        const ctrl = this.form.get(ItemsPerPageSelector);
        if (ctrl && this.cachedPaginationOption) {
            ctrl.setValue(this.cachedPaginationOption, {emitEvent: false});
        }
    }

    private initItemsPerPageFormControl(): FilterControl {
        this.initializedControls++;

        return new FilterControl({
            name: ItemsPerPageSelector,
            placeholder: 'Items per page',
            controls: [
                new SelectControl({
                    name: ItemsPerPageSelector,
                    value: this.defaultPaginationOption ?? DefaultItemsPerPage,
                    placeholder: 'Items per page',
                    validators: [],
                    componentType: ComponentTypesEnum.Select,
                    options: this.paginationOptions.map(item => ({ label: item + '', value: item }))
                })
            ],
            shown: true,
            sortIndex: 0
        });
    }

    private resetFilterControl(filterControl: FilterControl): { [key: string]: string | number } {
        filterControl.resetControls();

        return filterControl.controls.filter((formControl: BaseControl) => {
            return !formControl.disabled;
        }).reduce((reducedObject: { [key: string]: string | number }, formControl: BaseControl) => {
            if (formControl instanceof DatepickerControl && this.setDatePickersToNowOnClear) {
                formControl.value = moment().format();
            }

            if (formControl instanceof DatepickerControl && !formControl.value) {
                formControl.value = null;
            }

            reducedObject[formControl.name] = formControl.value;

            return reducedObject;
        }, {});
    }

    private validateForm(): boolean {
        const statusCheck = this.controls.reduce((reducedStatuses: boolean[], filterControl: FilterControl) => {
            if (filterControl.shown) {
                filterControl.controls.forEach((control: BaseControl) => {
                    const formControl = this.form.controls[control.name];

                    if (formControl && formControl.invalid) {
                        control.errors = Object.keys(formControl.errors).map((key: string) => {
                            return FormComponent.getFormMessages(control.placeholder)[key];
                        });

                        reducedStatuses.push(false);
                    }

                    reducedStatuses.push(true);
                });
            }

            return reducedStatuses;
        }, []);

        return (statusCheck.indexOf(false) < 0);
    }

    ngOnInit(): void {
        if (!this.form) {
            this.form = new FormGroup({});
        }

        if (typeof this.wantsValidation === undefined) {
            this.wantsValidation = false;
        }

        this.initFormAndControls();

        const valueChangesSubscription = this.form.valueChanges.subscribe((value: { [key: string]: string | number | boolean }) => {
            if (!this.isStatic) {
                if (!this.wantsValidation) {
                    this.filterChanged.emit(value);
                } else {
                    if (this.validateForm()) {
                        this.filterChanged.emit(value);
                    }
                }
            }

            this.valueChanged.emit(value);
        });

        const controlsValidationSubscription = this.controlsValidationChanged.subscribe((
            controlWithValidators: { name: string, validators: ValidatorFn[] }
        ) => {
            if (controlWithValidators) {
                const formsControl = this.form.get(controlWithValidators.name);

                if (!formsControl) {
                    this.form.addControl(controlWithValidators.name, new FormControl(null));
                }

                formsControl.clearValidators();
                formsControl.setValidators(controlWithValidators.validators);
                formsControl.updateValueAndValidity({ emitEvent: false });
            }
        });

        const updateValueAndValiditySubscription = this.updateValueAndValidity.subscribe((
            controlValue: {name: string, validators: ValidatorFn[], value:any, opts?:{onlySelf?: boolean; emitEvent?: boolean}}
        ) => {
            if (controlValue) {
                const formsControl = this.form.get(controlValue.name);
                formsControl.clearValidators();
                formsControl.setValidators(controlValue.validators);
                formsControl.setValue(controlValue.value, controlValue.opts);
                formsControl.updateValueAndValidity(controlValue.opts);
            }
        });

        if (this.controlsValueChangePair) {
            const controlValueChangeSubscription = this.form
                .get(this.controlsValueChangePair.triggerControlName)
                .valueChanges
                .subscribe(() => {
                    const filteredControls = this.controls.find((filterControl: FilterControl) => {
                        return filterControl.name === this.controlsValueChangePair.dependantControlName;
                    });

                    if (filteredControls) {
                        const initialControlValues = this.resetFilterControl(filteredControls);

                        this.form.patchValue(initialControlValues, { emitEvent: false });
                    }
                });

            this.subscriptionsArray.push(controlValueChangeSubscription);
        }

        this.subscriptionsArray.push(valueChangesSubscription, controlsValidationSubscription, updateValueAndValiditySubscription);

        this.afterInit.emit(true);
    }

    ngOnDestroy(): void {
        this.subscriptionsArray.forEach((subscription: Subscription) => subscription && subscription.unsubscribe());
    }

    onReset(): void {
        let filterControls = [];

        if (this.excludePaginationControls) {
            filterControls = this.controls.filter((filterControl: FilterControl) => {
                return filterControl.name !== ItemsPerPageSelector;
            });
        } else {
            filterControls = this.controls;
        }

        const initialFilterValues = filterControls.map((filterControl: FilterControl) => {
            return this.resetFilterControl(filterControl);
        }).reduce((reducedObject: { [key: string]: string | number }, filterValues: { [key: string]: string | number }) => {
            reducedObject = { ...reducedObject, ...filterValues };

            return reducedObject;
        }, {});

        this.form.patchValue(initialFilterValues, { emitEvent: false });
        this.clearTriggered.emit(this.form.value);
        if (!this.excludeFilterChangeOnClear){
            this.filterChanged.emit(this.form.value);
        }
    }

    toggleControl(filterControl: FilterControl): void {
        /**
         * Prevent filter execution during controls initialization
         */
        if (this.initializedControls < this.controls.length) {
            this.initializedControls++;
        } else {
            if (!filterControl.shown) {
                const reducedValues = filterControl.controls
                    .reduce((reducedObject: { [key: string]: string | number }, control: BaseControl) => {
                        reducedObject[control.name] = control.value;

                        return reducedObject;
                    }, {});

                this.form.patchValue(reducedValues, { emitEvent: false });
            } else {
                const initialControlValues = this.resetFilterControl(filterControl);

                this.form.patchValue(initialControlValues, { emitEvent: false });

                this.filterChanged.emit(this.form.value);
            }
        }
    }

    onClickOutsideDropdownBox(element: HTMLElement): void {
        if (this.isSettingsShown && !this.filterControlsBoxRef.nativeElement.contains(element)) {
            this.isSettingsShown = false;
        }
    }

    onFormSubmit(): void {
        if (this.validateForm()) {
            this.filterChanged.emit(this.form.value);
        }
    }
}

